import { ReactNode } from 'react';
import {
  AnalysisTypeEnum,
  AppointmentTypeEnum,
  type CallEditableFields,
  type CallMetadata,
  type CallRecord,
  CategoryEnum,
  ContactTypeEnum,
  findCitationTime,
  type IsFieldEdited,
  type KeyMomentType,
  SchedulingOpportunityEnum,
  SchedulingOutcomeEnum,
  SchedulingOutcomeV2Enum,
  SentimentEnum,
  ServiceQualityFlagEnum,
} from '@frontend/api-call-intel';

type FormSchedulingOutcome = SchedulingOutcomeEnum | 'none';

export const DEFAULT_FIELD_EMPTY_VALUE = '-';

export type CorrectionBannerConfig = {
  canMakeCorrection: boolean;
  isReviewed: boolean;
  showEditHistory: boolean;
};

export type Suggestion = {
  title: string;
  description: string;
};

type FieldMappingValue = {
  emptyValue: string;
  dependentAnalysis: AnalysisTypeEnum[];
  label: string;
  optionType: OptionType;
  type: keyof CallEditableFields;
  suggestion?: Suggestion;
};

export type FieldMapping = Partial<Record<AnalysisTypeEnum, FieldMappingValue>>;

export type OptionType =
  | 'appointmentTypes'
  | 'callDirection'
  | 'categories'
  | 'contactType'
  | 'feedbackIssueTypes'
  | 'schedulingOutcome'
  | 'schedulingOutcomeV2'
  | 'schedulingOpportunity'
  | 'sentiment'
  | 'taskStatus'
  | 'serviceQualityFlag';

export type RecordOptions = Record<string, ReactNode> | Record<string, string>;

export type BadgeOption = {
  chip: React.ReactNode;
  label?: string;
  submenuOptions?: Record<string, BadgeOption>;
};

export type FormState = {
  appointmentTypes: AppointmentTypeEnum[];
  categories: CategoryEnum[];
  contactType?: ContactTypeEnum;
  schedulingOpportunity?: SchedulingOpportunityEnum;
  schedulingOutcome?: FormSchedulingOutcome;
  schedulingOutcomeV2?: SchedulingOutcomeV2Enum;
  callSentiment?: SentimentEnum;
  serviceQualityFlags: ServiceQualityFlagEnum[];
};

export type KeyMomentRemovalChange = {
  [K in keyof FormState]-?: {
    id: string;
    field: K;
    keyMomentLabel: string;
    from: NonNullable<FormState[K]>;
    to: NonNullable<FormState[K]>;
  };
}[keyof FormState];

export const getFormStateByCall = (call?: CallRecord): FormState => ({
  appointmentTypes: call?.appointmentTypes ?? [],
  categories: call?.categories ?? [],
  contactType: call?.contactType,
  schedulingOpportunity:
    call?.callSchedulingOpportunity === null
      ? undefined
      : call?.callSchedulingOpportunity
      ? SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED
      : SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED,
  schedulingOutcome:
    call?.callSchedulingOutcome === null
      ? undefined
      : call?.callSchedulingOpportunity
      ? call?.callSchedulingOutcome
        ? SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED
        : SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED
      : 'none',
  schedulingOutcomeV2: call?.schedulingOutcomeV2,
  callSentiment: call?.callSentiment?.sentiment,
  serviceQualityFlags: call?.serviceQualityFlags ?? [],
});

/**
 * Transforms RecordOptions into a flat Record<string, BadgeOption>.
 */
export const transformOptionsToBadgeOptions = (
  options: RecordOptions,
  customLabels?: Record<string, string>
): Record<string, BadgeOption> => {
  return Object.entries(options).reduce<Record<string, BadgeOption>>((acc, [key, value]) => {
    acc[key] = {
      chip: value,
      label: customLabels?.[key] ?? '',
    };
    return acc;
  }, {});
};

/**
 * Transforms RecordOptions with nested submenus into Record<string, BadgeOption>.
 */
export const transformOptionsToNestedBadgeOptions = (
  optionsWithSubmenus: RecordOptions,
  suboptionsMap: Record<string, RecordOptions>,
  customLabels?: Record<string, string>
): Record<string, BadgeOption> => {
  return Object.entries(optionsWithSubmenus).reduce<Record<string, BadgeOption>>((acc, [key, value]) => {
    acc[key] = {
      chip: value,
      label: customLabels?.[key] ?? '',
      submenuOptions: suboptionsMap[key]
        ? Object.entries(suboptionsMap[key]).reduce<Record<string, BadgeOption>>((submenuAcc, [subKey, subValue]) => {
            submenuAcc[subKey] = {
              chip: subValue,
              label: customLabels?.[subKey] ?? '',
            };
            return submenuAcc;
          }, {})
        : undefined,
    };
    return acc;
  }, {});
};

/**
 * Replaces all empty string ("") values in an object with `undefined`.
 * Useful when working with forms where empty string fields should be treated as undefined.
 */

export const normalizeEmptyStrings = <T extends Record<string, any>>(obj: T): T => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value === '' ? undefined : value])) as T;
};

export const mapFormStateToCall = (form: FormState): Partial<CallRecord> => {
  return {
    appointmentTypes: form.appointmentTypes.length > 0 ? form.appointmentTypes : undefined,
    categories: form.categories.length > 0 ? form.categories : undefined,
    contactType: form.contactType,
    callSchedulingOpportunity:
      form.schedulingOpportunity === SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED,
    callSchedulingOutcome: form.schedulingOutcome === SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED,
    callSentiment: form.callSentiment ? { sentiment: form.callSentiment } : undefined,
    serviceQualityFlags: form.serviceQualityFlags.length > 0 ? form.serviceQualityFlags : undefined,
    schedulingOutcomeV2: form.schedulingOutcomeV2,
  };
};

export const computeIsFieldEdited = (currentEdited: IsFieldEdited, changedFields: (keyof CallEditableFields)[]) => {
  const updatedEdited: IsFieldEdited = { ...currentEdited };
  changedFields.forEach((field) => {
    const editedKey = fieldToEditedKeyMap[field];
    if (editedKey) {
      updatedEdited[editedKey] = true;
    }
  });
  return updatedEdited;
};

export const fieldToEditedKeyMap: Record<keyof CallEditableFields, keyof IsFieldEdited> = {
  appointmentTypes: 'isAppointmentTypeEdited',
  categories: 'isCategoryEdited',
  contactType: 'isContactTypeEdited',
  schedulingOpportunity: 'isSchedulingOpportunityEdited',
  schedulingOutcome: 'isSchedulingOutcomeEdited',
  callSentiment: 'isSentimentEdited',
  serviceQualityFlags: 'isServiceQualityFlagEdited',
  schedulingOutcomeV2: 'isSchedulingOutcomeEdited',
};

export const isUnscheduledKeyMomentRelevant = (schedulingOutcome?: SchedulingOutcomeV2Enum): boolean => {
  return (
    schedulingOutcome === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED ||
    schedulingOutcome === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
  );
};

export const hasKeyMomentReason = (reasons?: Array<{ description?: string }>): boolean => {
  return reasons?.some(({ description }) => !!description?.trim()) ?? false;
};

export const shouldRemoveKeyMoment = ({
  hasKeyMoment,
  isCurrentlyRelevant,
  willRemainRelevant,
}: {
  hasKeyMoment: boolean;
  isCurrentlyRelevant: boolean;
  willRemainRelevant: boolean;
}): boolean => {
  return hasKeyMoment && isCurrentlyRelevant && !willRemainRelevant;
};

export const shouldRemoveUnhappyKeyMoment = (call: CallRecord | undefined, nextSentiment?: SentimentEnum): boolean => {
  if (!call) {
    return false;
  }

  return shouldRemoveKeyMoment({
    hasKeyMoment: hasKeyMomentReason(call.callSentimentDetails?.reasons),
    isCurrentlyRelevant: call.callSentiment?.sentiment === SentimentEnum.SENTIMENT_NEGATIVE,
    willRemainRelevant: nextSentiment === SentimentEnum.SENTIMENT_NEGATIVE,
  });
};

export const shouldRemoveUnscheduledKeyMoment = (
  call: CallRecord | undefined,
  nextSchedulingOutcome?: SchedulingOutcomeV2Enum
): boolean => {
  if (!call) {
    return false;
  }

  const currentSchedulingOutcome = call.schedulingOutcomeV2;

  // The confirmation modal only applies when editing FROM Unscheduled.
  if (currentSchedulingOutcome !== SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED) {
    return false;
  }

  return shouldRemoveKeyMoment({
    hasKeyMoment: hasKeyMomentReason(call.schedulingOutcomeDetails?.reasons),
    // Since we already verified current outcome is Unscheduled,the Key Moment is currently relevant.
    isCurrentlyRelevant: true,
    // Unscheduled and Recovered both keep the original Unscheduled KM.
    willRemainRelevant: isUnscheduledKeyMomentRelevant(nextSchedulingOutcome),
  });
};

export const getKeyMomentCitationTime = ({
  callMetadata,
  keyMomentType,
  fallbackCitation,
}: {
  callMetadata?: CallMetadata;
  keyMomentType: KeyMomentType;
  fallbackCitation?: string;
}): string => {
  const existingCitationTime = callMetadata?.keyMoments?.find((keyMoment) => keyMoment.type === keyMomentType)
    ?.citations[0]?.time;

  if (existingCitationTime) {
    return existingCitationTime;
  }

  return fallbackCitation && callMetadata?.transcript
    ? findCitationTime(callMetadata.transcript, fallbackCitation)
    : '';
};

export const getHistoryReason = ({
  reasonText,
  citation,
  callMetadata,
  keyMomentType,
}: {
  reasonText: string;
  citation?: string;
  callMetadata?: CallMetadata;
  keyMomentType: KeyMomentType;
}): string | undefined => {
  if (!reasonText) return undefined;

  const citationTime = getKeyMomentCitationTime({
    callMetadata,
    keyMomentType,
    fallbackCitation: citation,
  });

  return citationTime ? `${reasonText} (${citationTime})` : reasonText;
};

export const isUnhappyKeyMomentRemoved = (call?: CallRecord): boolean => {
  if (!call?.isFieldEdited?.isSentimentEdited) {
    return false;
  }

  return (
    hasKeyMomentReason(call.callSentimentDetails?.reasons) &&
    call.callSentiment?.sentiment !== SentimentEnum.SENTIMENT_NEGATIVE
  );
};

export const isUnscheduledKeyMomentRemoved = (call?: CallRecord): boolean => {
  if (!call?.isFieldEdited?.isSchedulingOutcomeEdited) {
    return false;
  }

  return (
    hasKeyMomentReason(call.schedulingOutcomeDetails?.reasons) &&
    !isUnscheduledKeyMomentRelevant(call.schedulingOutcomeV2)
  );
};

export const hasRemovedKeyMoments = (call?: CallRecord): boolean => {
  return isUnhappyKeyMomentRemoved(call) || isUnscheduledKeyMomentRemoved(call);
};
